import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { loadYT } from "./PersistentPlayer";

/**
 * A single YouTube IFrame-API player that lives on a player's device — used for
 * both the hidden audio-only clip (G1/G2 audioSource=players) and the visible G4
 * videoclip (variant="video", a fixed banner).
 *
 * CRITICAL (iOS Safari): the player is created as soon as this mounts (during the
 * "Ready to Play" gate), so the user's tap can call play()/load() SYNCHRONOUSLY on
 * an already-initialized player — the only way iOS unlocks audio. The same player
 * instance is reused for the whole session, so later rounds play without a new tap.
 */
export const DeviceClip = forwardRef(function DeviceClip({ variant = "hidden", onReady, onState }, ref) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  const pendingRef = useRef(null); // intent captured before the player is ready

  useEffect(() => {
    let destroyed = false;
    loadYT().then((YT) => {
      if (destroyed || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        width: "100%",
        height: "100%",
        playerVars: { autoplay: 0, controls: 0, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3, fs: 0, disablekb: 1 },
        events: {
          onReady: () => {
            readyRef.current = true;
            const p = pendingRef.current;
            pendingRef.current = null;
            if (p) {
              try {
                if (p.action === "load") { playerRef.current.unMute(); playerRef.current.setVolume(100); playerRef.current.loadVideoById(p.id); }
                else if (p.action === "cue") { playerRef.current.cueVideoById(p.id); }
                else if (p.action === "play") { playerRef.current.unMute(); playerRef.current.setVolume(100); playerRef.current.playVideo(); }
              } catch {}
            }
            onReady?.();
          },
          onStateChange: (e) => onState?.(e.data),
        },
      });
    });
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy(); } catch {}
      playerRef.current = null;
      readyRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  useImperativeHandle(ref, () => ({
    isReady: () => readyRef.current,
    cue: (id) => { if (readyRef.current) { try { playerRef.current.cueVideoById(id); } catch {} } else pendingRef.current = { action: "cue", id }; },
    load: (id) => { if (readyRef.current) { try { playerRef.current.unMute(); playerRef.current.setVolume(100); playerRef.current.loadVideoById(id); } catch {} } else pendingRef.current = { action: "load", id }; },
    play: () => { if (readyRef.current) { try { playerRef.current.playVideo(); } catch {} } else if (!pendingRef.current) pendingRef.current = { action: "play" }; },
    pause: () => { try { playerRef.current?.pauseVideo(); } catch {} },
    unmute: () => { try { playerRef.current?.unMute(); playerRef.current?.setVolume(100); } catch {} },
    mute: () => { try { playerRef.current?.mute(); } catch {} },
  }));

  const wrapStyle = variant === "video"
    ? { position: "fixed", top: "145px", left: "50%", transform: "translateX(-50%)", width: "min(88vw, 420px)", aspectRatio: "16 / 9", zIndex: 35, borderRadius: 14, overflow: "hidden", border: "4px solid #1E1B4B", boxShadow: "0 10px 0 rgba(0,0,0,0.35)", background: "#0b1e3a" }
    : { position: "fixed", right: 2, bottom: 2, width: 10, height: 10, opacity: 0.01, pointerEvents: "none", zIndex: 0 };

  return (
    <div style={wrapStyle} aria-hidden={variant !== "video"}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      {variant === "video" && (
        <>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 34, background: "#1E1B4B", borderBottom: "2px solid rgba(255,251,235,0.2)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 10 }}>
            <span className="font-bebas" style={{ color: "#FFFBEB", fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>♪ Hitmaster Clip ♪</span>
          </div>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 30, background: "#1E1B4B", borderTop: "2px solid rgba(255,251,235,0.2)", pointerEvents: "none", zIndex: 10 }} />
        </>
      )}
    </div>
  );
});
